import { differenceInCalendarDays, format, parseISO, subDays } from 'date-fns'
import {
  IFuelBowserReconComputed,
  IFuelBowserReconDay,
  IFuelCustodyReconComputed,
  IFuelCustodyReconDay,
} from 'types/fuelCustody.interface'

export const FUEL_COVER_DANGER_DAYS = 3

export const FUEL_COVER_WARNING_DAYS = 7

export const FUEL_VARIANCE_TOLERANCE_PCT = 0.5

export const FUEL_BOWSER_TOLERANCE_PCT = 1.5

export const fuelReconDateISO = (daysAgo: number): string =>
  format(subDays(new Date(), daysAgo), 'yyyy-MM-dd')

export const fuelReconDateLabel = (iso: string): string => {
  const date = parseISO(iso)
  const diff = differenceInCalendarDays(new Date(), date)
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Yesterday'
  return format(date, 'd MMM')
}

export const computeFuelRecon = (
  day: IFuelCustodyReconDay,
  tolerancePct = FUEL_VARIANCE_TOLERANCE_PCT,
): IFuelCustodyReconComputed => {
  const transfers = day.transfers ?? []
  const transfersInL = transfers
    .filter((line) => line.direction === 'TRANSFER_IN')
    .reduce((sum, line) => sum + line.litres, 0)
  const transfersOutL = transfers
    .filter((line) => line.direction === 'TRANSFER_OUT')
    .reduce((sum, line) => sum + line.litres, 0)
  const bookStockL = day.openingDipL + day.deliveriesL + transfersInL - transfersOutL - day.dispensesL
  const varianceL = day.closingDipL - bookStockL
  const denominator = day.openingDipL + day.deliveriesL + transfersInL
  const rawPct = denominator > 0 ? (varianceL / denominator) * 100 : 0
  const rounded = Math.round(rawPct * 10) / 10
  const variancePct = rounded === 0 ? 0 : rounded
  return {
    ...day,
    transfers,
    transfersInL,
    transfersOutL,
    bookStockL,
    varianceL,
    variancePct,
    withinTolerance: Math.abs(variancePct) <= tolerancePct,
  }
}

export const computeBowserRecon = (
  day: IFuelBowserReconDay,
  tolerancePct = FUEL_BOWSER_TOLERANCE_PCT,
): IFuelBowserReconComputed => {
  const expectedReturnL = day.loadedL - day.dispensedL
  const varianceL = day.meteredReturnL - expectedReturnL
  const rawPct = day.loadedL > 0 ? (varianceL / day.loadedL) * 100 : 0
  const rounded = Math.round(rawPct * 10) / 10
  const variancePct = rounded === 0 ? 0 : rounded
  return {
    ...day,
    expectedReturnL,
    varianceL,
    variancePct,
    withinTolerance: Math.abs(variancePct) <= tolerancePct,
  }
}
